import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import {
  ChevronDown,
  Filter,
  Home,
  Bed,
  Bath,
  Ruler,
  DollarSign,
  Euro,
  EuroIcon,
  Circle,
} from "lucide-react";
import { useFilters } from "@/lib/FilterContext";
import { useState } from "react";
import { IconDoor } from "@tabler/icons-react";

export default function PropertyFilterBar({ className }) {
  const { filters, updateFilter } = useFilters();
  const [priceRange, setPriceRange] = useState(filters.price || [0, 1000000]);
  const [bedrooms, setBedrooms] = useState(filters.bedrooms || [0, 20]);
  const [bathrooms, setBathrooms] = useState(filters.bathrooms || [0, 20]);
  const [livingSpace, setLivingSpace] = useState(
    filters.livingSpace || [0, 50000]
  );

  const clampLivingSpaceValue = (value) => {
    if (Number.isNaN(value)) return 0;
    return Math.min(50000, Math.max(0, value));
  };

  const handleLivingSpaceInput = (index, rawValue) => {
    const numericValue = clampLivingSpaceValue(Number(rawValue));
    setLivingSpace((prev) => {
      const next = [...prev];
      next[index] = numericValue;

      if (next[0] > next[1]) {
        if (index === 0) {
          next[1] = numericValue;
        } else {
          next[0] = numericValue;
        }
      }

      updateFilter("livingSpace", next);
      return next;
    });
  };

  return (
    <div className=" -lg rounded-xl p-4 flex flex-col md:flex-row gap-4 items-center w-full">
      {/* Location Input */}
      <div className="flex w-full md:w-fit">
        <Input
          type="text"
          className="w-full bg-white md:w-64 rounded-r-none"
          value={filters.location}
          onChange={(e) => updateFilter("location", e.target.value)}
          placeholder="PLZ oder Ort eingeben..."
        />
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="flex rounded-l-none items-center gap-2"
            >
              <Filter size={16} /> <span className="md:block hidden"></span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-4  space-y-5">
            {/* Bedrooms */}
            <div className="flex flex-col items-start justify-center gap-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <IconDoor size={16} /> Zimmer: {bedrooms[0]} - {bedrooms[1]}
              </p>
              <Slider
                min={0}
                max={20}
                step={1}
                value={bedrooms}
                defaultValue={[0, 20]}
                onValueChange={(value) => {
                  setBedrooms(value);
                  updateFilter("bedrooms", value);
                }}
              />
            </div>

            {/* Bathrooms */}
            <div className="flex flex-col items-start justify-center gap-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <Bath size={16} /> Badezimmer: {bathrooms[0]} - {bathrooms[1]}
              </p>
              <Slider
                min={0}
                max={20}
                step={1}
                value={bathrooms}
                defaultValue={[0, 20]}
                onValueChange={(value) => {
                  setBathrooms(value);
                  updateFilter("bathrooms", value);
                }}
              />
            </div>

            {/* Living Space */}
            <div className="flex flex-col items-start justify-center gap-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <Ruler size={16} /> Wohnfläche (m²)
              </p>
              <div className="flex w-full gap-3 pt-1">
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={50000}
                  value={livingSpace[0]}
                  onChange={(event) =>
                    handleLivingSpaceInput(0, event.target.value)
                  }
                  className="bg-white"
                  placeholder="Min."
                />
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={50000}
                  value={livingSpace[1]}
                  onChange={(event) =>
                    handleLivingSpaceInput(1, event.target.value)
                  }
                  className="bg-white"
                  placeholder="Max."
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Price Range Dropdown */}
      <div className="flex items-center w-full gap-4">
        {/* Offer Type Select */}
        <Select
          className=""
          value={filters.offer_type}
          onValueChange={(value) => updateFilter("offer_type", value)}
        >
          <SelectTrigger className="flex w-full md:w-fit items-center bg-white gap-2">
            <Home size={16} /> <SelectValue placeholder="Verkaufstyp" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="BOTH">Beides</SelectItem>
            <SelectItem value="BUY">Zum Kaufen</SelectItem>
            <SelectItem value="RENT">Zum Mieten</SelectItem>
          </SelectContent>
        </Select>

        {/* Additional Filters Dropdown */}
        <div className="w-full max-w-[15rem]">
          <p className="mb-1 text-sm text-text-100">Preis</p>
          <Slider
            min={0}
            max={1000000}
            step={50000}
            value={priceRange}
            onValueChange={(value) => {
              setPriceRange(value);
              updateFilter("price", value);
            }}
          />
          <div className="flex justify-between text-xs text-gray-500 mt-2">
            <span>{priceRange[0].toLocaleString()}€</span>
            <span>{priceRange[1].toLocaleString()}€</span>
          </div>
        </div>
      </div>

      {/* Search Button */}
      {/* <Button className="bg-prime-700 text-white">Suchen</Button> */}
    </div>
  );
}
